import { type ReactNode } from 'react';
import { Pressable, Text as RNText, View } from 'react-native';
import Animated, { LinearTransition } from 'react-native-reanimated';

import { Badge } from '@/components/ui/Badge';
import { CheckCircle } from '@/components/ui/CheckCircle';
import { cn } from '@/components/ui/cn';
import { Text } from '@/components/ui/Text';

/** Duration of the row reorder (layout) animation. Shared so callers can lock
 *  toggling for exactly as long as rows are in motion. */
export const ROW_LAYOUT_DURATION_MS = 220;

export interface RunStepRowProps {
  label: string;
  checked: boolean;
  /** Attribution name shown when checked, e.g. "Jane". */
  by?: string | null;
  /** Clock stamp shown when checked, e.g. "9:18". */
  timeLabel?: string | null;
  /** Offline: the check is saved locally but not yet synced. */
  queued?: boolean;
  /** Sealed/immutable run — no toggle. */
  readOnly?: boolean;
  /** Temporarily blocks toggling while the reorder animation is playing. */
  disabled?: boolean;
  onToggle?: () => void;
  /** Quiet "+ Note" affordance. */
  onAddNote?: () => void;
  /** Notes thread / reference note rendered under the step. */
  children?: ReactNode;
  className?: string;
}

/** A single tappable run step with attribution, queued state, and notes slot. */
export function RunStepRow({
  label,
  checked,
  by,
  timeLabel,
  queued,
  readOnly,
  disabled,
  onToggle,
  onAddNote,
  children,
  className,
}: RunStepRowProps) {
  const locked = readOnly || disabled;
  return (
    <Animated.View
      layout={LinearTransition.duration(ROW_LAYOUT_DURATION_MS)}
      className={cn('gap-2 py-3', className)}
    >
      <View className="flex-row items-center gap-3">
        {/* Only the checkmark toggles — tapping the label does nothing, so a
            mis-tap while reading a step can't accidentally check it off. */}
        <Pressable
          onPress={locked ? undefined : onToggle}
          disabled={locked}
          hitSlop={10}
          accessibilityRole="checkbox"
          accessibilityState={{ checked, disabled: !!locked }}
          accessibilityLabel={label}
          className={cn(!locked && 'active:opacity-70')}
        >
          <CheckCircle checked={checked} />
        </Pressable>
        <Text
          variant="bodyMedium"
          numberOfLines={2}
          className={cn('flex-1', checked && 'text-muted line-through')}
        >
          {label}
        </Text>

        {queued ? (
          <Badge label="queued" tone="neutral" />
        ) : checked && (timeLabel || by) ? (
          <View className="max-w-[40%] items-end">
            {timeLabel ? <Text variant="accentSmall">{timeLabel}</Text> : null}
            {by ? (
              <Text variant="accentSmall" numberOfLines={1} className="text-faint">
                {by}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>

      {children}

      {onAddNote && !readOnly ? (
        <Pressable onPress={onAddNote} hitSlop={6} className="ml-9 self-start">
          <RNText className="font-sans-semibold text-sm text-muted">+ Note</RNText>
        </Pressable>
      ) : null}
    </Animated.View>
  );
}
